import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { BookingRequestDto } from '@wingseat/shared';
import { ConfirmButton } from '../components/ConfirmButton';
import {
  useBookingRequestsForMyFlights,
  useCancelBooking,
  useConfirmBooking,
  useMyBookingRequests,
  useRejectBooking,
} from '../hooks/use-bookings';
import { formatDateTime } from '../lib/format';

type Tab = 'mine' | 'forMyFlights';

const STATUS_STYLES: Record<BookingRequestDto['status'], string> = {
  REQUESTED: 'bg-blue-900/40 text-blue-300 border-blue-800',
  WAITLISTED: 'bg-amber-900/40 text-amber-300 border-amber-800',
  CONFIRMED: 'bg-emerald-900/40 text-emerald-300 border-emerald-800',
  REJECTED: 'bg-rose-900/40 text-rose-300 border-rose-800',
  CANCELLED: 'bg-slate-800 text-slate-400 border-slate-700',
};

export function MyRequestsPage() {
  const [tab, setTab] = useState<Tab>('mine');
  const mine = useMyBookingRequests();
  const incoming = useBookingRequestsForMyFlights();
  const confirm = useConfirmBooking();
  const reject = useRejectBooking();
  const cancel = useCancelBooking();

  const list = tab === 'mine' ? mine.data : incoming.data;
  const loading = tab === 'mine' ? mine.isLoading : incoming.isLoading;

  return (
    <div className="px-4 py-4 space-y-3">
      <h1 className="text-xl font-semibold">Anfragen</h1>

      <div className="flex gap-1 rounded-xl border border-slate-800 bg-slate-900/60 p-1">
        <TabButton active={tab === 'mine'} onClick={() => setTab('mine')}>
          Meine Anfragen
        </TabButton>
        <TabButton
          active={tab === 'forMyFlights'}
          onClick={() => setTab('forMyFlights')}
        >
          Auf meine Flüge
        </TabButton>
      </div>

      {loading ? (
        <p className="py-12 text-center text-sm text-slate-400">Lädt …</p>
      ) : !list || list.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-400">
          Keine Anfragen.
        </p>
      ) : (
        <ul className="space-y-3">
          {list.map((b) => (
            <li
              key={b.id}
              className="rounded-xl border border-slate-800 bg-slate-900/60 p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {tab === 'forMyFlights' && b.requester && (
                    <p className="text-sm font-medium">
                      {b.requester.firstName} {b.requester.lastName}
                    </p>
                  )}
                  <Link
                    to={`/app/flights/${b.flightOfferId}`}
                    className="mt-0.5 text-xs text-brand-400 hover:underline"
                  >
                    Flug öffnen →
                  </Link>
                  <p className="mt-1 text-xs text-slate-500">
                    {formatDateTime(b.createdAt)}
                  </p>
                  {b.message && (
                    <p className="mt-2 text-xs italic text-slate-300">
                      „{b.message}"
                    </p>
                  )}
                  {b.decisionReason && (
                    <p className="mt-1 text-xs text-slate-400">
                      Begründung: {b.decisionReason}
                    </p>
                  )}
                </div>
                <span
                  className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_STYLES[b.status]}`}
                >
                  {b.status}
                </span>
              </div>

              {tab === 'forMyFlights' &&
                (b.status === 'REQUESTED' || b.status === 'WAITLISTED') && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => confirm.mutate({ id: b.id })}
                      disabled={confirm.isPending}
                      className="flex-1 rounded-lg bg-emerald-700 px-3 py-2 text-sm font-medium hover:bg-emerald-600 disabled:opacity-60"
                    >
                      Bestätigen
                    </button>
                    <div className="flex-1">
                      <ConfirmButton
                        label="Ablehnen"
                        withReason
                        reasonPlaceholder="Begründung (optional)"
                        confirmLabel="Ablehnen bestätigen"
                        disabled={reject.isPending}
                        onConfirm={(reason) => reject.mutate({ id: b.id, reason })}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}

              {tab === 'mine' &&
                (b.status === 'REQUESTED' ||
                  b.status === 'WAITLISTED' ||
                  b.status === 'CONFIRMED') && (
                  <div className="mt-3">
                    <ConfirmButton
                      label="Stornieren"
                      variant="neutral"
                      confirmLabel="Wirklich stornieren?"
                      disabled={cancel.isPending}
                      onConfirm={() => cancel.mutate(b.id)}
                      className="w-full"
                    />
                  </div>
                )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-lg px-3 py-2 text-sm transition-colors ${
        active
          ? 'bg-slate-800 text-slate-100'
          : 'text-slate-400 hover:text-slate-200'
      }`}
    >
      {children}
    </button>
  );
}
